import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Search, UserX, PhoneCall, StickyNote, ArrowLeft, CheckCircle2, Filter, Users, AlertTriangle, CalendarClock, RefreshCw, Download, FileText, MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';

/** Format an Oman/international phone into wa.me-compatible digits only. */
const cleanPhone = (raw) => {
  if (!raw) return '';
  let p = String(raw).trim().replace(/[\s\-()]/g, '');
  if (p.startsWith('+')) p = p.slice(1);
  // Local Omani formats: 8 digits starting with 7/9/2 → prefix 968.
  if (/^[79]\d{7}$/.test(p)) p = '968' + p;
  return p.replace(/\D/g, '');
};

/** Build a friendly Arabic WhatsApp message for an inactive reader.
 *  If a template is provided, substitute {variables}; otherwise use a
 *  sensible default that mirrors the admin-editable template.
 *  Empty values fall back to graceful Arabic strings instead of raw {vars}. */
const buildWaMessage = (r, template) => {
  const missedAbs = Math.abs(Number(r.missed_quota) || 0);
  const ctx = {
    name: r.name || 'أخي/أختي',
    title: r.title || '',
    inactive_days: (r.inactive_days != null && r.inactive_days !== '') ? r.inactive_days : 'فترة طويلة',
    missed_quota: r.missed_quota != null ? r.missed_quota : '0',
    missed_abs: missedAbs,
    reader_number: r.reader_number || '',
  };
  const raw = template && template.trim() ? template : (
    r.gender === 'أنثى'
      ? 'السلام عليكن ورحمة الله وبركاته، {name} الكريمة\n\nلاحظنا في مبادرة «غصن الزيتون القرائية» أنكِ لم تُشاركي تقريراً منذ {inactive_days} يوماً.\nفوائتكِ الحالية: {missed_abs}.\n\nإن كان هناك ما يحول بينكِ وبين المواصلة فأخبرينا حتى نساعدكِ — وفّقكِ الله وأعانكِ على ما تحبين. 🌿'
      : 'السلام عليكم ورحمة الله وبركاته، {name} الكريم\n\nلاحظنا في مبادرة «غصن الزيتون القرائية» أنك لم تُشارك تقريراً منذ {inactive_days} يوماً.\nفوائتك الحالية: {missed_abs}.\n\nإن كان هناك ما يحول بينك وبين المواصلة فأخبرنا حتى نساعدك — وفّقك الله وأعانك على ما تحب. 🌿'
  );
  return raw.replace(/\{(\w+)\}/g, (m, key) => (key in ctx ? String(ctx[key]) : m));
};

/** Trigger a browser download of a UTF-8 CSV (Excel/Sheets-friendly with BOM). */
const downloadCSV = (rows, filename) => {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map(r => r.map(escape).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const FILTERS_KEY = 'admin-inactive-filters-v1';
const readSavedFilters = () => {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return {
      days: Number.isFinite(Number(p.days)) ? Number(p.days) : 14,
      minMissed: Number.isFinite(Number(p.minMissed)) ? Number(p.minMissed) : 0,
      gender: ['all', 'ذكر', 'أنثى'].includes(p.gender) ? p.gender : 'all',
      onlyUncontacted: !!p.onlyUncontacted,
    };
  } catch { return null; }
};

const AdminInactivePage = () => {
  const navigate = useNavigate();
  const saved = readSavedFilters();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [days, setDays] = useState(saved?.days ?? 14);
  const [minMissed, setMinMissed] = useState(saved?.minMissed ?? 0);
  const [gender, setGender] = useState(saved?.gender ?? 'all');
  const [onlyUncontacted, setOnlyUncontacted] = useState(saved?.onlyUncontacted ?? false);
  const [search, setSearch] = useState('');
  const [savingRn, setSavingRn] = useState('');
  const [openNotesRn, setOpenNotesRn] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [exporting, setExporting] = useState(false);
  const [waTemplates, setWaTemplates] = useState({ male: '', female: '' });

  // Persist filter selections so the next visit remembers the last query.
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify({ days, minMissed, gender, onlyUncontacted }));
    } catch { /* ignore quota errors */ }
  }, [days, minMissed, gender, onlyUncontacted]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days, min_missed: minMissed, gender, only_uncontacted: onlyUncontacted };
      const { data } = await api.get('/admin/inactive-readers', { params });
      setItems(data?.items || []);
      setCount(data?.count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل الحسابات الخاملة');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Load editable WhatsApp templates once.
  useEffect(() => {
    api.get('/admin/whatsapp-templates')
      .then((res) => setWaTemplates({ male: res.data?.male || '', female: res.data?.female || '' }))
      .catch(() => { /* keep client-side defaults */ });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return items;
    return items.filter(it =>
      (it.name || '').includes(q) ||
      (it.reader_number || '').includes(q) ||
      (it.phone || '').includes(q) ||
      (it.title || '').includes(q)
    );
  }, [items, search]);

  const toggleContacted = async (rn, next) => {
    setSavingRn(rn);
    try {
      const { data } = await api.post(`/admin/inactive-readers/${rn}`, { contacted: next });
      setItems(prev => prev.map(x => x.reader_number === rn ? { ...x, contacted: !!data.contacted, last_contact_at: data.updated_at, last_contact_by: data.updated_by_name || data.updated_by } : x));
      toast.success(next ? 'تم التأشير على التواصل' : 'تم إلغاء علامة التواصل');
    } catch (err) {
      toast.error('فشل الحفظ');
    } finally {
      setSavingRn('');
    }
  };

  const openNotes = (rn, current) => {
    setOpenNotesRn(rn);
    setNoteDraft(current || '');
  };
  const saveNote = async (rn) => {
    setSavingRn(rn);
    try {
      const { data } = await api.post(`/admin/inactive-readers/${rn}`, { notes: noteDraft });
      setItems(prev => prev.map(x => x.reader_number === rn ? { ...x, notes: noteDraft, last_contact_at: data.updated_at, last_contact_by: data.updated_by_name || data.updated_by } : x));
      setOpenNotesRn('');
      toast.success('تم حفظ الملاحظة');
    } catch {
      toast.error('فشل حفظ الملاحظة');
    } finally {
      setSavingRn('');
    }
  };

  const openReader = (rn) => navigate(`/admin/readers?reader=${rn}`);

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.info('لا توجد بيانات لتصديرها');
      return;
    }
    setExporting(true);
    try {
      const headers = [
        'الرقم', 'الاسم', 'الجنس', 'اللقب', 'الهاتف', 'البريد',
        'التحدي', 'الإنجاز', 'آخر تقرير', 'أيام الخمول', 'الفوائت',
        'تاريخ الانضمام', 'حالة التواصل', 'الملاحظات', 'آخر تحديث', 'المسؤول',
      ];
      const rows = [headers, ...filtered.map(r => [
        r.reader_number, r.name, r.gender, r.title, r.phone, r.email,
        r.challenge, r.total_achievements, r.last_report_date || '',
        r.inactive_days ?? '', r.missed_quota,
        r.join_date ? r.join_date.slice(0, 10) : '',
        r.contacted ? 'تم التواصل' : 'بانتظار التواصل',
        r.notes || '',
        r.last_contact_at ? new Date(r.last_contact_at).toISOString().slice(0, 10) : '',
        r.last_contact_by || '',
      ])];
      const today = new Date().toISOString().slice(0, 10);
      downloadCSV(rows, `الحسابات_الخاملة_${today}.csv`);
      toast.success('تم تصدير الملف — يمكنك رفعه إلى Google Sheets مباشرة');
    } catch (err) {
      toast.error('فشل التصدير');
    } finally {
      setExporting(false);
    }
  };

  const summary = useMemo(() => {
    const c = items.filter(x => x.contacted).length;
    return { total: items.length, contacted: c, uncontacted: items.length - c };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="admin-inactive-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-accent flex items-center gap-2">
            <UserX className="w-7 h-7 text-rose-500" /> الحسابات الخاملة
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            متابعة القراء الذين انقطعوا عن رفع التقارير أو تراكمت عليهم فوائت — لتيسير التواصل الشخصي معهم.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={handleExport}
            disabled={exporting || filtered.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="export-inactive-btn"
            title="حفظ النتائج كـ CSV (يمكن رفعه مباشرة إلى Google Sheets)"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'جارٍ التصدير...' : `تصدير إلى Google Sheets (${filtered.length})`}
          </Button>
          <Button onClick={load} disabled={loading} variant="outline" className="gap-2" data-testid="reload-inactive-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> تحديث
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        <Card className="p-4 bg-gradient-to-br from-rose-50 to-white border-rose-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center"><Users className="w-5 h-5 text-rose-600" /></div>
            <div>
              <p className="text-xs text-rose-700/70">المطابقون للفلاتر</p>
              <p className="text-2xl font-bold text-rose-700">{summary.total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-xs text-emerald-700/70">تم التواصل معهم</p>
              <p className="text-2xl font-bold text-emerald-700">{summary.contacted}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-amber-700/70">بانتظار التواصل</p>
              <p className="text-2xl font-bold text-amber-700">{summary.uncontacted}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 md:p-5">
        <div className="flex items-center gap-2 text-accent mb-1">
          <Filter className="w-4 h-4" /> <h3 className="font-bold">الفلاتر</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          يمكنك استخدام فلتر «الأيام» أو «الفوائت» منفصلاً — اترك الحقل صفراً لتعطيله. إن عُطّل الاثنان، تظهر الحسابات الخاملة أكثر من 14 يوماً افتراضياً.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">لم يقدّم تقريراً منذ (بالأيام) — 0 لتعطيله</label>
            <Input type="number" min="0" max="365" value={days} onChange={(e) => setDays(Math.max(0, Number(e.target.value) || 0))} data-testid="filter-days" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">حد أدنى للفوائت — 0 لتعطيله</label>
            <Input type="number" min="0" max="500" value={minMissed} onChange={(e) => setMinMissed(Math.max(0, Number(e.target.value) || 0))} data-testid="filter-missed" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">الجنس</label>
            <select value={gender} onChange={(e) => setGender(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="filter-gender">
              <option value="all">الكل</option>
              <option value="male">ذكور</option>
              <option value="female">إناث</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm text-accent cursor-pointer select-none">
              <input type="checkbox" checked={onlyUncontacted} onChange={(e) => setOnlyUncontacted(e.target.checked)} data-testid="filter-uncontacted" />
              فقط غير المتواصل معهم
            </label>
          </div>
        </div>
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 mt-4">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم/الرقم/الهاتف/اللقب..." className="pr-9" data-testid="inactive-search" />
          </div>
          <Button onClick={load} className="bg-primary hover:bg-primary/90 gap-2" data-testid="apply-filters-btn">
            <Filter className="w-4 h-4" /> تطبيق الفلاتر
          </Button>
        </div>
      </Card>

      <p className="text-muted-foreground text-sm">
        {loading ? 'جارٍ التحميل...' : `عرض ${filtered.length} من أصل ${count} حساب مطابق للفلاتر.`}
      </p>

      <div className="grid grid-cols-1 gap-3">
        {filtered.map((r) => (
          <Card key={r.reader_number}
            className={`border transition-all hover:shadow-md overflow-hidden ${r.contacted ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-gray-200'}`}
            data-testid={`inactive-card-${r.reader_number}`}>
            <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
              <button
                onClick={() => openReader(r.reader_number)}
                className="flex-1 text-right group"
                data-testid={`goto-reader-${r.reader_number}`}
                title="فتح الحساب في إدارة القراء"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold text-accent group-hover:text-primary transition-colors">
                    {r.name || '—'}
                  </h3>
                  <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full font-mono">#{r.reader_number}</span>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{r.gender}</span>
                  {r.title && <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full">{r.title}</span>}
                </div>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />
                    آخر تقرير: <b className="text-accent">{r.last_report_date || 'لا يوجد'}</b>
                    {r.inactive_days != null && <span className="text-rose-600 font-semibold">(قبل {r.inactive_days} يوماً)</span>}
                  </span>
                  <span>الفوائت: <b className={`${r.missed_quota < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{r.missed_quota}</b></span>
                  <span>الإنجاز: <b className="text-accent">{r.total_achievements}/{r.challenge}</b></span>
                  {r.phone && <span className="font-mono">{r.phone}</span>}
                </div>
                {r.notes && (
                  <div className="mt-2 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
                    <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span className="whitespace-pre-wrap">{r.notes}</span>
                  </div>
                )}
                {r.last_contact_at && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    آخر تحديث: {new Date(r.last_contact_at).toLocaleDateString('ar-OM')} {r.last_contact_by && `· ${r.last_contact_by}`}
                  </p>
                )}
              </button>

              <div className="flex items-center gap-2 md:flex-col md:items-stretch shrink-0">
                {(() => {
                  const digits = cleanPhone(r.phone);
                  const disabled = !digits;
                  const tpl = r.gender === 'أنثى' ? waTemplates.female : waTemplates.male;
                  const href = digits ? `https://wa.me/${digits}?text=${encodeURIComponent(buildWaMessage(r, tpl))}` : undefined;
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (disabled) {
                          e.preventDefault();
                          toast.info('لا يوجد رقم هاتف مسجّل لهذا الحساب');
                          return;
                        }
                        // Auto-mark as contacted after opening WhatsApp (nice touch).
                        if (!r.contacted) toggleContacted(r.reader_number, true);
                      }}
                      className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium transition-colors ${
                        disabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-auto'
                          : 'bg-[#25D366] hover:bg-[#1FBA57] text-white'
                      }`}
                      data-testid={`whatsapp-${r.reader_number}`}
                      title={disabled ? 'لا يوجد رقم مسجّل' : 'إرسال رسالة واتساب جاهزة'}
                    >
                      <MessageCircle className="w-4 h-4" /> واتساب
                    </a>
                  );
                })()}
                <Button
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); toggleContacted(r.reader_number, !r.contacted); }}
                  disabled={savingRn === r.reader_number}
                  className={`gap-1 ${r.contacted ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-primary hover:bg-primary/90 text-white'}`}
                  data-testid={`toggle-contacted-${r.reader_number}`}
                >
                  <PhoneCall className="w-4 h-4" /> {r.contacted ? 'تم التواصل ✓' : 'تأشير كمتواصَل معه'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); openNotes(r.reader_number, r.notes); }}
                  className="gap-1"
                  data-testid={`edit-notes-${r.reader_number}`}
                >
                  <StickyNote className="w-4 h-4" /> {r.notes ? 'تعديل الملاحظة' : 'إضافة ملاحظة'}
                </Button>
              </div>
            </div>

            {openNotesRn === r.reader_number && (
              <div className="mx-4 border-t pt-3 pb-3">
                <Textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="ملاحظات: سبب الخمول، وسيلة التواصل، الوعد بالمتابعة..."
                  rows={3}
                  data-testid={`notes-textarea-${r.reader_number}`}
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" onClick={() => saveNote(r.reader_number)} disabled={savingRn === r.reader_number} className="bg-primary hover:bg-primary/90" data-testid={`save-notes-${r.reader_number}`}>حفظ</Button>
                  <Button size="sm" variant="ghost" onClick={() => setOpenNotesRn('')}>إلغاء</Button>
                </div>
              </div>
            )}

            {/* Footer band — clearly inside the card, opens the full reader profile */}
            <button
              onClick={() => openReader(r.reader_number)}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-xs font-semibold border-t border-dashed border-primary/25 bg-primary/5 hover:bg-primary/10 text-primary transition-colors group"
              data-testid={`open-in-admin-${r.reader_number}`}
            >
              <span className="flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                فتح الملف الكامل في «إدارة القراء»
              </span>
              <ArrowLeft className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:-translate-x-0.5 transition-all" />
            </button>
          </Card>
        ))}
        {filtered.length === 0 && !loading && (
          <Card className="p-10 text-center text-muted-foreground">لا توجد حسابات مطابقة للفلاتر الحالية</Card>
        )}
      </div>
    </div>
  );
};

export default AdminInactivePage;
